import { useCallback, useEffect, useState } from 'react';
import type { ReconciliationReport } from '@/types/manifest';
import { getReport } from '../api/manifests-api';

interface UseManifestReportResult {
  report: ReconciliationReport | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useManifestReport(manifestId: number | null): UseManifestReportResult {
  const [report, setReport] = useState<ReconciliationReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchReport = useCallback(async () => {
    if (manifestId === null) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await getReport(manifestId);
      if (response.success && response.data) {
        setReport(response.data);
      } else {
        setError(response.message || 'Failed to fetch report');
      }
    } catch {
      setError('An error occurred while fetching report');
    } finally {
      setIsLoading(false);
    }
  }, [manifestId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  return {
    report,
    isLoading,
    error,
    refetch: fetchReport,
  };
}
